import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, ArrowRight } from "lucide-react";
import api from "../lib/api";

// Prompt 15 PART C — compact Dashboard tile summarising the three
// buckets of attention-grabbing items so an operator sees them without
// scanning every table.
// - connection reconnects (expired / revoked tokens)
// - push "Failed" invoices (BC rejected a vendor, etc.)
// - held invoices awaiting approval or mapping
// Clicking a bucket deep-links into the filtered list.
export default function NeedsAttentionTile() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const r = await api.get("/alerts/attention");
        if (alive) setData(r.data);
      } catch (_e) { /* silent */ }
    };
    load();
    const t = setInterval(load, 45000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  if (!data || data.total === 0) return null;
  const reconnects = (data.needs_reconnect || []).length;

  return (
    <section
      className="card-flat p-6 border-l-4 border-l-red-600 bg-red-50/30"
      data-testid="needs-attention-tile"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="text-red-600 shrink-0 mt-0.5" size={18} />
          <div>
            <div className="label-mini text-red-700">needs attention</div>
            <h2 className="font-display text-xl font-semibold mt-1">
              {data.total} {data.total === 1 ? "item" : "items"} to resolve
            </h2>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-5">
        <AttentionCell
          label="GL connections"
          value={reconnects}
          hint={reconnects === 0 ? "all healthy" : "expired or revoked — reconnect"}
          onClick={() => navigate("/connections")}
          testid="needs-attention-reconnects"
          disabled={reconnects === 0}
        />
        <AttentionCell
          label="failed pushes"
          value={data.failed_count || 0}
          hint="Business Central / QuickBooks rejected — see error"
          onClick={() => navigate("/invoices?sync_status=failed")}
          testid="needs-attention-failed"
          disabled={(data.failed_count || 0) === 0}
        />
        <AttentionCell
          label="held invoices"
          value={data.held_count || 0}
          hint="needs mapping / approval"
          onClick={() => navigate("/invoices?sync_status=held")}
          testid="needs-attention-held"
          disabled={(data.held_count || 0) === 0}
        />
      </div>
    </section>
  );
}

function AttentionCell({ label, value, hint, onClick, testid, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`text-left border p-4 flex items-center justify-between gap-3 transition-colors ${
        disabled
          ? "border-neutral-200 bg-white opacity-60 cursor-default"
          : "border-neutral-900 bg-white hover:bg-neutral-50"
      }`}
      data-testid={testid}
    >
      <div className="min-w-0">
        <div className="label-mini">{label}</div>
        <div className={`font-display font-semibold text-3xl mt-1 ${
          value > 0 ? "text-red-700" : "text-neutral-400"
        }`}>
          {value}
        </div>
        <div className="text-xs text-neutral-500 mt-1 truncate">{hint}</div>
      </div>
      {!disabled && <ArrowRight size={14} className="shrink-0" />}
    </button>
  );
}
